/**
 * ROAR-4943 (ROAR-4899 §4 decision 8) — AC3's `(feature_key, target_entity_type)` → route map.
 *
 * Per the P1.8 data-flows document the backend returns stable IDs only and "the frontend maps
 * (feature_key, target_entity_type) → route (don't store URLs in DB)". This module is that map and
 * nothing else.
 *
 * **Row lifecycle is deliberately absent.** Whether a row is clickable — settled, with a target id,
 * and mapped — is `components/Settings/CreditsUsage/isLedgerRowClickable.js`. Keeping settlement
 * out of the registry means a future `status` value (`expired`, `refunded`) is a change to that
 * helper, not to the routes, and AC3's "settled" rule stays visible in the ledger component.
 *
 * **The `/app` prefix is hard-coded, and that is only sound because Settings is customer-only.**
 * Elsewhere the repo derives it per role via `getMainURL` (`/app` | `/admin` | `/tester`,
 * `components/Header/Notifications/notification-helper.js:378`). The Settings menu item is gated to
 * `[SIEMBA_CUSTOMER, SIEMBA_CUSTOMER_ENGINEER]` (§4 decision 6), both of which resolve to `/app`,
 * so no role lookup is needed here. If that gate ever widens to Siemba staff, this must take the
 * prefix as an argument.
 *
 * **The KEY NAMES are provisional**, exactly as `api/queries/account.js` records for Login
 * Activity: the backend's `feature_key` / `target_entity_type` vocabulary is not yet agreed (the
 * Story's own open question). The route TARGETS are grounded — all four exist in
 * `containers/Routes/MyDashboardRoutes.js:74-75,119-120`. This is the single place a rename lands,
 * a one-file change in ROAR-4945 once the contract is confirmed.
 */

/** `/app/genpt/tests/:session_id` — the run itself (MyDashboardRoutes.js:119). */
const genptSessionRoute = (id) => `/app/genpt/tests/${encodeURIComponent(id)}`;

/** `/app/genpt/tests/:session_id/findings` — the run's findings (MyDashboardRoutes.js:120). */
const genptSessionFindingsRoute = (id) => `/app/genpt/tests/${encodeURIComponent(id)}/findings`;

/**
 * `/app/easm/monitoring/:id/:tabElement` (MyDashboardRoutes.js:74). `summary` is the tab every
 * other entry point into this screen uses (e.g. `EASMMonitoringData.js:115`).
 */
const easmDomainRoute = (id) => `/app/easm/monitoring/${encodeURIComponent(id)}/summary`;

/** `/app/easm/attack_surface/subdomain/:id` (MyDashboardRoutes.js:75). */
const easmSubdomainRoute = (id) => `/app/easm/attack_surface/subdomain/${encodeURIComponent(id)}`;

/**
 * `{ [feature_key]: { [target_entity_type]: (id) => path } }`.
 *
 * Nested rather than composite-keyed so the supported pairs are greppable; the builders themselves
 * are shared by reference, so wiring a feature key to an existing target duplicates no logic.
 */
export const USAGE_LEDGER_ROUTES = {
  genpt_test: {
    genpt_session: genptSessionRoute,
    genpt_session_findings: genptSessionFindingsRoute,
  },
  easm_scan: {
    easm_domain: easmDomainRoute,
    easm_subdomain: easmSubdomainRoute,
  },
};

/**
 * The route builder for a ledger row's pair, or null when the pair is not mapped.
 *
 * Returning null — rather than guessing a route — is what keeps an unrecognized activity type from
 * sending the user somewhere wrong; the row renders as plain text instead.
 *
 * @param {string} featureKey Row's `feature_key`.
 * @param {string} targetEntityType Row's `target_entity_type`.
 * @returns {((id: string) => string)|null} Builder, or null if unmapped.
 */
export const resolveUsageLedgerRoute = (featureKey, targetEntityType) => {
  // `hasOwnProperty` guards, not plain lookups: `USAGE_LEDGER_ROUTES['toString']` would otherwise
  // hand back an Object.prototype member and pass a `typeof === 'function'` check.
  if (!Object.prototype.hasOwnProperty.call(USAGE_LEDGER_ROUTES, featureKey)) return null;
  const byEntityType = USAGE_LEDGER_ROUTES[featureKey];
  if (!Object.prototype.hasOwnProperty.call(byEntityType, targetEntityType)) return null;
  return byEntityType[targetEntityType];
};
